import { t } from "i18next";
import React, { useEffect, useState } from "react";
import styles from "styles.module.css";
import { UserInformation } from "ui/userprofile/UserInformation";
import { NotificationPage } from "ui/userprofile/Notification";
import { getUserProfileApi, getUserVesselsApi } from "api/user";
import { UserProfile, UserRole } from "interface/user";
import {
	getGetInternationalCountryCodeApi,
	getProductsPagingApi,
} from "api/common";
import { InternationalCountryCode, Product } from "interface/common";
import { IUserProfileVessel } from "interface/vessel";
import { IProduct } from "interface/product";
import { ChangeRegistration } from "ui/userprofile/ChangeRegistration";
import { ProductGrid } from "ui/userprofile/Products";
import { ChangePassword } from "ui/userprofile/ChangePassword";
import { Loading } from "ui/Loading";
import {
	addProductToProfileApi,
	deleteProductFromProfileApi,
	deleteSmsProductFromProfileApi,
	getProductsByBuyerApi,
	getProfileSmsProductsApi,
} from "api/product";
import CustomStepper from "ui/CustomStepper";
import { useNavigate } from "react-router";
import { PageTypePath } from "utils/pages";
import CustomButton from "ui/Button";
import { COLOR, ICON_NAMES } from "utils/constant";

enum UserProfileSettingTabs {
	Information = "UserInformation",
	Notification = "Notifications",
	ChangeRegistration = "ChangeRegistration",
	UserProducts = "UserProducts",
	Password = "Password",
}

const settingsTabs = Object.values(UserProfileSettingTabs);

export const UserProfileSetting = () => {
	const [user, setUser] = React.useState<UserProfile | null>(null);
	const [countryCodes, setCountryCodes] = React.useState<
		InternationalCountryCode[]
	>([]);
	const [selectedTab, setSelectedTab] = React.useState<UserProfileSettingTabs>(
		UserProfileSettingTabs.Information,
	);
	const [Vessels, setVessels] = React.useState<IUserProfileVessel[]>([]);
	const [smsProducts, setSmsProducts] = React.useState<IProduct[]>([]);
	const [buyerProducts, setBuyerProducts] = React.useState<Product[]>([]);
	const [allProducts, setAllProducts] = React.useState<Product[]>([]);
	const [updateSmsProducts, setUpdateSmsProducts] = React.useState(false);
	const [availableProducts, setAvailableProducts] = React.useState<Product[]>(
		[],
	);
	const [loadingUser, setLoadingUser] = React.useState(true);
	const [tabs, setTabs] = React.useState(settingsTabs);
	const [allProductsFilter, setAllProductsFilter] = React.useState({
		take: 20,
		skip: 0,
		text: "", // Added text property
	});
	const [step, setStep] = useState(0);
	const navigate = useNavigate();

	useEffect(() => {
		if (!user) {
			getUserProfileApi()
				.then((response) => {
					setUser(response.data);
					setLoadingUser(false);
				})
				.catch((error) => {
					console.log(error);
				})
				.finally(() => {
					setLoadingUser(false);
				});
		}

		getGetInternationalCountryCodeApi()
			.then((response) => {
				setCountryCodes(response);
			})
			.catch((error) => {
				console.log(error);
			});
		if (
			user?.userGroup &&
			user.userGroup.filter(
				(role) => role.vessel || role.buyer || role.terminal,
			).length > 0
		) {
			getUserVesselsApi()
				.then((response) => {
					setVessels(response.data);
				})
				.catch((error) => {
					console.log(error);
				});
			getProfileSmsProductsApi()
				.then((response) => {
					setSmsProducts(response.data);
				})
				.catch((error) => {
					console.log(error);
				});
		}

		handleTabs();
	}, [user]);

	useEffect(() => {
		if (
			user?.userGroup &&
			user.userGroup.filter(
				(role) => role.vessel || role.buyer || role.terminal,
			).length > 0
		) {
			getAllProducts();
		}
	}, [allProductsFilter, user?.userGroup]);

	useEffect(() => {
		if (
			user?.userGroup &&
			user.userGroup.filter(
				(role) => role.vessel || role.buyer || role.terminal,
			).length > 0
		) {
			getBuyerProducts();
		}
	}, [user?.userGroup]);

	useEffect(() => {
		if (allProducts.length > 0) {
			const filtered = allProducts.filter(
				(product) => !buyerProducts.some((buyer) => buyer.id === product.id),
			);
			setAvailableProducts(filtered);
		} else {
			setAvailableProducts([]);
		}
	}, [allProducts, buyerProducts]);

	const handleDeleteProduct = (id: number) => {
		deleteSmsProductFromProfileApi(id as any)
			.then(() => {
				getProfileSmsProductsApi()
					.then((response) => {
						setSmsProducts(response.data);
					})
					.catch((error) => {
						console.log(error);
					});
			})
			.catch((error) => {
				console.error("Error deleting product:", error);
			});
	};

	const handleTabs = () => {
		const _tabs = settingsTabs.filter((tab) => {
			if (tab === UserProfileSettingTabs.ChangeRegistration) {
				return (
					user?.userGroup &&
					user.userGroup.filter((role) => role.vessel).length > 0
				);
			}
			if (tab === UserProfileSettingTabs.UserProducts) {
				return (
					user?.userGroup &&
					user.userGroup.filter(
						(role) => role.vessel || role.buyer || role.terminal,
					).length > 0
				);
			}
			if (tab === UserProfileSettingTabs.Notification) {
				return (
					user?.userGroup &&
					user.userGroup.filter((role) => role.vessel || role.buyer).length > 0
				);
			}
			return true;
		});
		setTabs(_tabs);
	};

	const handleAddProductToBuyer = (product: Product) => {
		addProductToProfileApi(product.id as any)
			.then(() => {
				getBuyerProducts();
			})
			.catch((error) => {
				console.log(error);
			});
	};
	const handleDeleteBuyerProduct = (data: Product) => {
		deleteProductFromProfileApi(data.id as any)
			.then(() => {
				console.log("Product deleted successfully:", data);
				getAllProducts();
				getBuyerProducts();
			})
			.catch((error) => {
				console.log(error);
			});
	};

	const getBuyerProducts = () => {
		getProductsByBuyerApi()
			.then((response) => {
				setBuyerProducts(response.data);
			})
			.catch((error) => {
				console.log(error);
			});
	};

	const getAllProducts = () => {
		getProductsPagingApi(allProductsFilter)
			.then((response) => {
				setAllProducts(response.data);
			})
			.catch((error) => {
				console.log(error);
			});
	};
	const handleStepTabs = (step: number) => {
		const tab = tabs[step];
		setSelectedTab(tab);
		setStep(step);
	};

	const handleNextStep = () => {
		if (step < tabs.length - 1) {
			handleStepTabs(step + 1);
			return;
		}

		navigate(`/`);
	};

	const handlePreviousStep = () => {
		if (step > 0) {
			handleStepTabs(step - 1);
		}
	};

	const isFirstStep = step === 0;
	const isLastStep = step === tabs.length - 1;

	if (loadingUser) return <Loading />;
	return (
		<div
			style={{
				maxHeight: "90%",
				height: "calc(100dvh - 250px)",
				display: "flex",
				flexDirection: "column",
				justifyContent: "space-between",
			}}
			className={styles.PageContainer}
		>
			<div>
				<div
					className="page-header"
					// style={{ borderBottom: "1px solid #dee2e6" }}
				>
					<legend className={styles.BoxHeadingLarge}>{t("UserProfile")}</legend>
				</div>
				<div style={{ marginTop: "20px" }}>
					<CustomStepper
						steps={tabs.map((tab) => ({
							label: t(tab),
							optional: false,
						}))}
						activeStep={step}
						setActiveStep={handleStepTabs}
					/>

					<div style={{ padding: "20px" }}>
						{selectedTab === UserProfileSettingTabs.Information && user && (
							<UserInformation
								user={user as any}
								setUser={setUser}
								countryCodes={countryCodes.map(
									(code) => code.internationalDialing,
								)}
								setCountryCode={() => {}}
							/>
						)}
						{selectedTab === UserProfileSettingTabs.Notification && (
							<>
								<div
									style={{
										fontWeight: "bold",
										fontSize: "18px",
										marginBottom: "10px",
										textAlign: "center",
									}}
								>
									{t("Notifications")}
								</div>
								<NotificationPage data={user?.notifications || []} />
							</>
						)}
						{selectedTab === UserProfileSettingTabs.ChangeRegistration && (
							<div>
								<div style={{ marginBottom: "10px", textAlign: "center" }}>
									<ChangeRegistration
										vessel={Vessels}
										products={smsProducts}
										setSmsProducts={setSmsProducts}
										countryCodes={countryCodes}
										deleteProduct={handleDeleteProduct}
									/>
								</div>
							</div>
						)}
						{selectedTab === UserProfileSettingTabs.UserProducts && (
							<div
								style={{
									display: "flex",
									gap: "50px",
									width: "100%",
									height: "100%",
									overflow: "auto",
								}}
							>
								<ProductGrid
									data={availableProducts}
									page={{ ...allProductsFilter }}
									setPage={setAllProductsFilter}
									btnIconName={ICON_NAMES.Plus}
									btnClick={handleAddProductToBuyer}
									headerText={t("SelectYourProduct")}
								/>
								<ProductGrid
									data={buyerProducts}
									btnIconName={ICON_NAMES.Delete}
									headerText={t("YourProducts")}
									btnClick={handleDeleteBuyerProduct}
									showAllProducts={false}
									hideVirtualScroll={true}
								/>
							</div>
						)}
						{selectedTab === UserProfileSettingTabs.Password && (
							<div>
								<ChangePassword />
							</div>
						)}
					</div>
				</div>
			</div>
			<div
				style={{
					display: "flex",
					justifyContent: "space-between",
					padding: "20px",
					marginBottom: 0,
					backgroundColor: "#fff",
				}}
			>
				<CustomButton
					label={t("Previous")}
					onClick={handlePreviousStep}
					disabled={isFirstStep}
					bgColor={isFirstStep ? "#ccc" : undefined}
				/>
				<CustomButton
					label={isLastStep ? t("Complete") : t("Next")}
					onClick={handleNextStep}
					// disabled={isLastStep}
					labelStyle={{
						color: "white",
					}}
					bgColor={COLOR.Green}
				/>
			</div>
		</div>
	);
};
